import React from "react";
import { ShieldAlert } from "lucide-react";
import type { InsightCoverageWarning, InsightProvenanceMember } from "../../types/insightApi";

export interface CoverageWarningBannerProps {
	warnings?: InsightCoverageWarning[];
	/** Used only to resolve a warning's `ctxrUuids` to readable member names — never to re-derive the warning itself. */
	provenance?: InsightProvenanceMember[];
}

function memberNames(ctxrUuids: string[] | undefined, provenance: InsightProvenanceMember[]): string | null {
	if (!ctxrUuids || ctxrUuids.length === 0) return null;
	const names = provenance.filter((member) => ctxrUuids.includes(member.ctxrUuid)).map((member) => member.name);
	return names.length > 0 ? names.join(", ") : null;
}

/**
 * Non-blocking Discovery/CSPM coverage-gap warning (CTX-128 — e.g. a missing
 * `DescribeSecurityGroups`-class permission for one account/region/resource
 * family). Unlike `StatusBanner` (a whole contributing member failed to
 * produce topology at all), this renders alongside a topology that DID load
 * successfully — the gap is scoped to one resource family, not the entire
 * source. Only the processor's curated `message`/`remediation` text is ever
 * shown — never a raw internal/stack-trace error — and each warning is
 * collapsed behind its own `<details>` so the canvas isn't crowded by
 * default while remediation context stays one click away.
 */
export const CoverageWarningBanner: React.FC<CoverageWarningBannerProps> = ({ warnings = [], provenance = [] }) => {
	if (warnings.length === 0) return null;

	return (
		<div
			data-testid="coverage-warning-banner"
			role="status"
			className="flex flex-col gap-1.5 rounded-sm border border-semantic-border-warning-secondary bg-semantic-bg-warning-secondary px-3 py-2 text-sm text-semantic-text-warning"
		>
			{warnings.map((warning) => {
				const affectedMembers = memberNames(warning.ctxrUuids, provenance);
				const scope = [warning.account, warning.region].filter(Boolean).join(" / ");
				return (
					<details key={warning.id} data-testid={`coverage-warning-${warning.id}`}>
						<summary className="flex cursor-pointer list-none items-start gap-2">
							<ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
							<span className="min-w-0 flex-1">
								<span className="font-medium">{warning.capability} unavailable</span>
								{scope && <span className="ml-1 text-xs text-semantic-text-warning">({scope})</span>}
							</span>
						</summary>
						<div className="ml-6 mt-1 flex flex-col gap-0.5 text-xs">
							<p>{warning.message}</p>
							{affectedMembers && <p>Affects: {affectedMembers}</p>}
							{warning.remediation && <p className="italic">{warning.remediation}</p>}
						</div>
					</details>
				);
			})}
		</div>
	);
};

export default CoverageWarningBanner;
